/** Shared presentational primitives. */
import {
  Children, cloneElement, createContext, isValidElement, useContext, useState, useCallback, useId, useMemo, useRef,
} from 'react'
import { Link } from 'react-router-dom'
import Dialog from '@mui/material/Dialog'
import { FieldContext, joinIds, useFieldDescription } from './fieldContext'
import { useCanBrowse } from '../lib/viewmode'
import { useRelabel, usePartnerTerm, relabelText, currentTerm, codeText, useCode } from '../lib/terms'
import { csvCell } from '../lib/csv'
import { Icon, isIconName } from './Icon'
import { EnumSelect } from './Select'

/**
 * Prose handed to a component as a prop rather than as children — a confirmation's body,
 * a back button's words. A component of its own so the hook has somewhere to live.
 */
const Relabelled = ({ children }) => {
  const t = useRelabel()
  return t(children)
}

// ---------------------------------------------------------------- cards & tiles

/**
 * A titled panel.
 *
 * Pass `onToggle` to make it fold away: the title block becomes the control, the way a
 * Filters panel's summary row is, and the body is not rendered while it is shut. The
 * caller owns `open`, because when a card should be open is a question about the page —
 * on the updates report it follows which partner unit is selected — and not something a
 * card can work out for itself. `actions` stay beside the title and keep their own
 * clicks; they are a sibling of the toggle, not inside it.
 */
export const Card = ({
  title, sub, actions, children, footer, className = '', bodyClass = '', open = true, onToggle,
}) => {
  /* The card's own words follow the installation's term; its children do not. A
     card's body is where records are drawn — an update's text, a partner's own
     description — and those say what was written. See lib/terms.jsx. */
  const t = useRelabel()
  const foldable = !!onToggle
  const shut = foldable && !open
  const titles = (
    <>
      {title && <h2>{t(title)}</h2>}
      {sub && <div className="card-sub">{t(sub)}</div>}
    </>
  )
  return (
    <section className={`card ${foldable ? 'card-foldable ' : ''}${shut ? 'shut ' : ''}${className}`}>
      {(title || actions) && (
        <div className="card-head card-head-tight">
          {foldable ? (
            <button type="button" className="card-head-title card-head-toggle" onClick={onToggle} aria-expanded={open}>
              <span className="card-caret" aria-hidden="true"><Icon name="chevron" size={16} /></span>
              <span className="card-head-titles">{titles}</span>
            </button>
          ) : (
            <div className="card-head-title">{titles}</div>
          )}
          {actions && <div className="row row-tight">{actions}</div>}
        </div>
      )}
      {!shut && <div className={`card-body ${bodyClass}`}>{children}</div>}
      {footer && <div className="card-foot">{footer}</div>}
    </section>
  )
}

/**
 * A number with a label. `to` makes the whole tile a link; `onClick` makes it a filter —
 * a real button, which is what gives the coverage report's buckets a keyboard-reachable
 * way in to the same selection its chart offers by mouse. `active` is the pressed state,
 * so a tile that is filtering the page says so.
 */
/** A figure a tile can draw at full size: a number, formatted or not, or the em dash. */
const isCount = (v) => typeof v === 'number' || (typeof v === 'string' && /^[\d,.\s%—+]+$/.test(v))

export function Tile({ label, value, note, to, tone, onClick, active, title }) {
  // Label, note and tooltip are written here; `value` is a number off a query. The
  // tooltip goes through relabelText rather than the node walker, because a `title`
  // attribute is a string and not a tree.
  const t = useRelabel()
  const term = usePartnerTerm()
  const inner = (
    <>
      <div className="tile-label">{t(label)}</div>
      <div
        className={isCount(value) ? 'tile-value' : 'tile-value tile-value-text'}
        style={tone ? { color: `var(--${tone})` } : undefined}
      >
        {value}
      </div>
      {note && <div className="tile-note">{t(note)}</div>}
    </>
  )
  if (to) {
    return (
      <Link className="tile" to={to} title={relabelText(title, term)}>
        {inner}
      </Link>
    )
  }
  if (onClick) {
    return (
      <button
        type="button"
        className={`tile tile-button${active ? ' active' : ''}`}
        onClick={onClick}
        aria-pressed={!!active}
        title={relabelText(title, term)}
      >
        {inner}
      </button>
    )
  }
  return <div className="tile">{inner}</div>
}

export const Badge = ({ tone, dot, children, title, style }) => (
  <span
    className={`badge${tone ? ` badge-${tone}` : ''}${dot ? ' badge-dot' : ''}`}
    title={title}
    style={style}
  >
    {children}
  </span>
)

// ---------------------------------------------------------------- states

export const Spinner = ({ label }) => (
  <span className="row row-tight">
    <span className="spinner" />
    {label && <span className="small muted">{label}</span>}
  </span>
)

export const Loading = ({ label = 'Loading…', rows = 4 }) => (
  <div className="col" aria-busy="true" aria-live="polite">
    <Spinner label={label} />
    {Array.from({ length: rows }, (_, i) => (
      <div key={i} className="skeleton" style={{ width: `${100 - i * 11}%` }} />
    ))}
  </div>
)

export const Empty = ({ icon = 'empty', title = 'Nothing here', children }) => {
  // Both halves are this application explaining an absence, so both follow the term.
  const t = useRelabel()
  return (
    <div className="empty">
      <div className="empty-icon">{isIconName(icon) ? <Icon name={icon} size={32} /> : icon}</div>
      <div className="empty-title">{t(title)}</div>
      {children && <div className="small">{t(children)}</div>}
    </div>
  )
}

/* The icon a notice carries when it names none: its tone's. `icon={null}` asks for none. */
const TONE_ICON = { warn: 'warning', danger: 'error', ok: 'check', info: 'info' }

export const Notice = ({ tone, icon, title, children }) => {
  /* A notice is prose, always ours, and often the Worker's — an error message or an
     import summary arrives here as text like any other, which is how the server comes
     to speak the installation's term without knowing it exists. */
  const t = useRelabel()
  const shown = icon === undefined ? TONE_ICON[tone] ?? 'info' : icon
  return (
    <div className={`notice${tone ? ` notice-${tone}` : ''}`} role={tone === 'danger' ? 'alert' : undefined}>
      {shown && (
        <span className="notice-icon">{isIconName(shown) ? <Icon name={shown} size={18} /> : shown}</span>
      )}
      <div className="notice-body">
        {title && <div className="notice-title">{t(title)}</div>}
        {t(children)}
      </div>
    </div>
  )
}

/**
 * An expired sign-in is the one failure a retry cannot touch.
 *
 * Refetching sends the same request into the same closed door, which is exactly the dead
 * end this replaces: a red card offering a Try again that could only fail again. The cure
 * is a document navigation, because that is the request Access answers with the login
 * page — see reauthenticationPlan in lib/api.js. Warn rather than danger, and its own
 * wording, because needing to sign in again is the system working.
 *
 * Reads as slightly odd on the reads, where api() has already started the reload and this
 * is on screen for the moment before it lands. Left in rather than blanked, since the
 * moment is not always brief — a slow login redirect is still a screen somebody is looking
 * at, and the button does what the page is already doing.
 */
export const ErrorState = ({ error, retry }) =>
  error?.sessionExpired ? (
    <Notice tone="warn" icon="lock" title={error.message || 'Your sign-in has expired'}>
      {error.detail && <div className="small">{String(error.detail)}</div>}
      <button
        className="btn btn-sm btn-primary"
        style={{ marginTop: 8 }}
        onClick={() => window.location.reload()}
      >
        Sign in again
      </button>
    </Notice>
  ) : (
    <Notice tone="danger" icon="warning" title={error?.message || 'Something went wrong'}>
      {error?.detail && <div className="small mono">{String(error.detail)}</div>}
      {retry && (
        <button className="btn btn-sm" style={{ marginTop: 8 }} onClick={retry}>
          Try again
        </button>
      )}
    </Notice>
  )

/** Renders loading / error / empty / content for a react-query result. */
export function QueryState({ query, children, emptyWhen, empty }) {
  if (query.isLoading) return <Loading />
  if (query.isError) return <ErrorState error={query.error} retry={query.refetch} />
  if (emptyWhen && emptyWhen(query.data)) return empty ?? <Empty />
  return children(query.data)
}

// ---------------------------------------------------------------- tabs

/**
 * A row of tabs, keyboard-driven the way the ARIA tabs pattern expects: Tab enters the
 * row on the selected tab and leaves it again (the others are tabIndex -1), and the arrow
 * keys, Home and End move between tabs. Selection follows focus, since switching a tab
 * only redraws what is already loaded on the page.
 */
export const Tabs = ({ tabs, active, onChange }) => {
  const t = useRelabel()
  const refs = useRef([])
  // The tab that holds the row's one tab stop: the selected one, or the first if none is.
  const stop = Math.max(0, tabs.findIndex((tab) => tab.key === active))
  const onKeyDown = (e, i) => {
    const last = tabs.length - 1
    const to = {
      ArrowRight: i === last ? 0 : i + 1,
      ArrowLeft: i === 0 ? last : i - 1,
      Home: 0,
      End: last,
    }[e.key]
    if (to === undefined) return
    e.preventDefault()
    refs.current[to]?.focus()
    onChange(tabs[to].key)
  }
  return (
    <div className="tabs" role="tablist" aria-orientation="horizontal">
      {tabs.map((tab, i) => (
        <button
          key={tab.key}
          type="button"
          role="tab"
          ref={(el) => {
            refs.current[i] = el
          }}
          tabIndex={i === stop ? 0 : -1}
          aria-selected={active === tab.key}
          className={`tab${active === tab.key ? ' active' : ''}`}
          onClick={() => onChange(tab.key)}
          onKeyDown={(e) => onKeyDown(e, i)}
        >
          {t(tab.label)}
          {tab.count !== undefined && <span className="subtle"> ({tab.count})</span>}
        </button>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------- modal

/**
 * Open lists that own Escape before the dialog does but are not MUI modals themselves:
 * a searchable picker's list (Autocomplete's popper) and the desktop calendar
 * (components/DateField.jsx). Both close on Escape, but only stop the key reaching the
 * dialog when focus is inside the list — with focus still in the field, the dialog hears
 * it first. A plain select's menu (a Popover) and the touch-screen calendar (a Dialog) are
 * MUI modals, and MUI already gives Escape to whichever modal is on top.
 */
const OPEN_LIST = '.MuiAutocomplete-popper, .MuiPickerPopper-root'

/**
 * A dialog, drawn by MUI's Dialog: it traps focus inside while open, puts focus back on
 * whatever opened it when it closes, hides the page behind it from screen readers, locks
 * the page's scroll, and gives Escape to the topmost dialog only — so a lightbox opened
 * from the file importer, or a confirmation on top of an image, closes alone.
 *
 * It portals to <body>, as the hand-written one did and for the same reason: a dialog
 * left where it was written takes its layer from whatever encloses it, and one opened
 * from the sidebar (.nav, a stacking context that scrolls) was pinned to the sidebar's
 * column and drawn underneath the page. On <body>, --z-modal means what styles.css says,
 * and the pickers, which portal there too, sit above it on --z-dropdown.
 *
 * Not rendered at all while shut, rather than handed open={false}: MUI would keep the
 * children mounted through its exit transition, and callers write their bodies against
 * the record being edited (`open={!!editing}`), which is gone by then.
 *
 * The look is the archive's own .modal, not MUI's Paper — see `.modal-scrim` in styles.css.
 */
export function Modal({ open, onClose, title, children, footer, size }) {
  const t = useRelabel()
  const titleId = useId()
  if (!open) return null
  return (
    <Dialog
      open
      onClose={(_, reason) => {
        // Escape with a list open closes the list, not the form underneath it.
        if (reason === 'escapeKeyDown' && document.querySelector(OPEN_LIST)) return
        onClose?.()
      }}
      maxWidth={false}
      aria-labelledby={titleId}
      className="modal-scrim"
      // The archive's ladder rather than the theme's zIndex.modal, which is --z-dropdown's
      // 400 so that the pickers' lists clear a dialog (lib/muiTheme.js).
      sx={{ zIndex: 'var(--z-modal)' }}
      slotProps={{
        backdrop: { className: 'modal-backdrop' },
        container: { className: 'modal-frame' },
        paper: { className: `modal${size ? ` modal-${size}` : ''}`, elevation: 0 },
      }}
    >
      <div className="modal-head">
        {/* The dialog's own words. Its body is not relabelled here — a dialog is
            where a record is edited or previewed — but a ConfirmModal's prose and an
            InfoButton's note are, below. */}
        <h2 id={titleId}>{t(title)}</h2>
        <button type="button" className="btn btn-ghost btn-icon btn-sm" onClick={onClose} aria-label="Close">
          ✕
        </button>
      </div>
      <div className="modal-body">{children}</div>
      {footer && <div className="modal-foot">{footer}</div>}
    </Dialog>
  )
}

/**
 * An explanation kept behind an ⓘ. For notes that are worth having to hand but
 * not worth the vertical space they take up on every visit.
 */
export function InfoButton({ title, children, size = 'sm' }) {
  const t = useRelabel()
  const term = usePartnerTerm()
  const [open, setOpen] = useState(false)
  const heading = typeof title === 'string' ? relabelText(title, term) : title
  return (
    <>
      <button
        type="button"
        className="btn btn-sm btn-icon btn-ghost info-button"
        onClick={() => setOpen(true)}
        aria-label={heading ? `About ${String(heading).toLowerCase()}` : 'More information'}
        title={heading || 'More information'}
      >
        ⓘ
      </button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={heading}
        size={size}
        footer={
          <button className="btn" onClick={() => setOpen(false)}>
            Close
          </button>
        }
      >
        {t(children)}
      </Modal>
    </>
  )
}

/** Confirmation dialog for destructive actions. */
export const ConfirmModal = ({ open, onClose, onConfirm, title, body, confirmLabel = 'Delete', busy, confirmDisabled }) => (
  <Modal
    open={open}
    onClose={onClose}
    title={title}
    footer={
      <>
        <button className="btn btn-danger" onClick={onConfirm} disabled={busy || confirmDisabled}>
          {busy ? <Spinner /> : confirmLabel}
        </button>
        <button className="btn" onClick={onClose} disabled={busy}>
          Cancel
        </button>
      </>
    }
  >
    {/* What is about to happen, in the installation's term — a Modal does not relabel
        its own body, and this one is prose rather than a record. */}
    <Relabelled>{body}</Relabelled>
  </Modal>
)

// ---------------------------------------------------------------- form bits

/* The elements a Field can wire up by itself, when one is written directly inside it. */
const HOST_CONTROLS = new Set(['input', 'textarea', 'select'])

/**
 * A labelled control with an optional hint or error under it.
 *
 * The hint or error is tied to the control with aria-describedby, so a screen reader reads
 * it out with the control rather than leaving it for someone to stumble on. A plain
 * <input>, <textarea> or <select> written directly inside is wired here; the pickers,
 * DateField, Switch and SearchInput read the id from FieldContext (fieldContext.js) and
 * put it on their own focusable element. Anything else — a row of buttons, a grid of
 * pictures — is a group the hint describes as a whole, and is left alone.
 */
export const Field = ({ label, hint, error, required, children, htmlFor }) => {
  // The label, the hint and the complaint are all written here. The control inside is
  // where a record's own value lives, and it is left alone.
  const t = useRelabel()
  const baseId = useId()
  const noteId = error ? `${baseId}-error` : hint ? `${baseId}-hint` : undefined
  const context = useMemo(() => ({ describedBy: noteId, invalid: !!error }), [noteId, error])
  // Always mapped, even with nothing to add, so a hint turning into an error does not
  // change the children's keys and remount the input that is being typed into.
  const controls = Children.map(children, (child) =>
    isValidElement(child) && HOST_CONTROLS.has(child.type) && noteId
      ? cloneElement(child, {
          'aria-describedby': joinIds(child.props['aria-describedby'], noteId),
          'aria-invalid': child.props['aria-invalid'] ?? (error ? true : undefined),
        })
      : child
  )
  return (
    <div className="field">
      {label && (
        /* The id is for the pickers (components/Select.jsx): MUI's Select puts the id
           `htmlFor` names on a hidden input, so its visible box is named through
           aria-labelledby instead, and the searchable pickers do the same to match. */
        <label className="field-label" htmlFor={htmlFor} id={htmlFor ? `${htmlFor}-label` : undefined}>
          {t(label)}
          {required && <span className="field-req" title="Required">*</span>}
        </label>
      )}
      <FieldContext.Provider value={context}>{controls}</FieldContext.Provider>
      {error ? (
        <div className="field-error" id={noteId}>{t(error)}</div>
      ) : hint ? (
        <div className="field-hint" id={noteId}>{t(hint)}</div>
      ) : null}
    </div>
  )
}

export const Switch = ({ checked, onChange, label, disabled, title }) => {
  const t = useRelabel()
  const term = usePartnerTerm()
  const { describedBy } = useFieldDescription()
  return (
    <label className="switch" title={relabelText(title, term)}>
      <input
        type="checkbox"
        aria-describedby={describedBy}
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="switch-track">
        <span className="switch-thumb" />
      </span>
      {label && <span>{t(label)}</span>}
    </label>
  )
}

const alumniTitle = (where) => `Include ${where} from records marked as alumni`

/**
 * The alumni opt-in, in one place because it appears on nine pages and the wording is
 * the promise: every page leaves alumni out until this is switched on. Repeating the
 * markup was how the two pages that had it already drifted to different labels.
 *
 * `where` names what is being hidden on the page it sits on — "updates", "records" —
 * since "Hidden" alone does not say hidden from what.
 *
 * "Alumni records" rather than "Alumni partner units": the registry holds training schools
 * and projects too, and either can be alumni. It is also the reason this label no longer
 * goes through the relabeller — that setting renames an installation's *partners*, and
 * this switch is no longer only about them.
 */
export const AlumniToggle = ({ checked, onChange, where = 'records', disabled, hint, title }) => (
  <Field
    label="Alumni records"
    hint={hint ?? `Their ${where} are left out by default.`}
  >
    <Switch
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      label={checked ? 'Shown' : 'Hidden'}
      title={title ?? alumniTitle(where)}
    />
  </Field>
)

/**
 * The same switch for a page with no filter panel to put it in — it goes in the header
 * actions, so it carries its own word rather than borrowing a Field label for one.
 */
export const AlumniSwitch = ({ checked, onChange, where = 'records' }) => (
  <Switch
    checked={checked}
    onChange={onChange}
    label={`Alumni ${checked ? 'shown' : 'hidden'}`}
    title={alumniTitle(where)}
  />
)

/**
 * The counterpart to the toggle: what the default is holding back, said out loud.
 * Renders nothing at zero, so a page with no alumni behind it stays quiet.
 */
export const AlumniHidden = ({ count, noun = 'records' }) => {
  const t = useRelabel()
  return count ? (
    <span className="small muted">
      {' · '}
      {t(`${fmtInt(count)} ${noun} from alumni records hidden`)}
    </span>
  ) : null
}

export const Checkbox = ({ checked, onChange, label, disabled }) => {
  const t = useRelabel()
  return (
    <label className="checkbox">
      <input type="checkbox" checked={!!checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      {label && <span>{t(label)}</span>}
    </label>
  )
}

export const SearchInput = ({ value, onChange, placeholder = 'Search…', ...rest }) => {
  const term = usePartnerTerm()
  const { describedBy } = useFieldDescription()
  return (
    <div className="search-input">
      <Icon name="search" size={16} className="icon search-input-icon" />
      <input
        className="input"
        type="search"
        value={value ?? ''}
        placeholder={relabelText(placeholder, term)}
        onChange={(e) => onChange(e.target.value)}
        {...rest}
        aria-describedby={joinIds(rest['aria-describedby'], describedBy)}
        title={relabelText(rest.title, term) ?? rest.title}
      />
    </div>
  )
}

// ---------------------------------------------------------------- filters

/** How many fields of a filter state object are actually set. */
// `false` is excluded along with the empty values: a toggle that is off is not a
// filter someone applied, and counting it would have every page open claiming a
// filter is already narrowing the results.
export const countFilters = (f) =>
  Object.values(f).filter((v) =>
    Array.isArray(v) ? v.length > 0 : v !== '' && v !== null && v !== undefined && v !== false
  ).length

/**
 * A filter grid that folds up when it is in the way. `active` keeps the count of
 * applied filters visible while it is shut, so nothing silently narrows the results.
 *
 * Shut every time you arrive: every page that has one of these opens on the records,
 * and the panel stood a screen of controls between the page and its own content — on a
 * phone, often the whole first screen. A page whose panel *is* its controls rather
 * than a filter over a list passes defaultOpen (see AI studio), and that one opens on
 * arrival for the same reason the others do not.
 *
 * Opening one is therefore a fact about this visit and nothing longer. It was
 * remembered under `s4n.filters.<id>` and is not any more: a panel opened once to
 * change one field was then open on every later visit to that page, which is the state
 * the fold exists to get out of. Nothing is hidden by that — the badge on the summary
 * row counts what is applied whether the panel is open or shut. Old keys left in a
 * browser are simply not read. `id` stays, because the ARIA wiring below needs a name
 * and every page passes one already.
 *
 * What makes "this visit" the right unit is the shell: the routes are wrapped in a
 * boundary keyed on `location.pathname`, so leaving a page for another unmounts this
 * and returning builds it again, shut. A filter that only moves the query string
 * leaves the pathname alone, so working in an open panel never closes it.
 */
export function Filters({ id, active = 0, defaultOpen = false, label = 'Filters', onClear, children }) {
  const t = useRelabel()
  const [open, setOpen] = useState(defaultOpen)
  const bodyId = `filters-${id}-body`

  const toggle = () => setOpen((v) => !v)

  return (
    /* The summary is a row of two controls rather than one, which it has to be: the ✕
       cannot live inside the toggle, a button inside a button being neither valid nor
       clickable. */
    <section className={`filters-panel${open ? ' open' : ''}`}>
      <div className="filters-summary-row">
        <button
          type="button"
          className="filters-summary"
          onClick={toggle}
          aria-expanded={open}
          aria-controls={bodyId}
        >
          <span className="filters-caret" aria-hidden="true">
            <Icon name="chevron" size={16} />
          </span>
          <span>{t(label)}</span>
          {active > 0 && <Badge tone="accent">{active} applied</Badge>}
          <span className="spacer" />
          <span className="small subtle">{open ? 'Hide' : 'Show'}</span>
        </button>
        {/* Only with something to clear, and only if the caller says what clearing means.
            The panel has a Clear filters button of its own inside it; this is the same
            action without having to open the panel to reach it, which is the whole point
            of a filter you can see the count of while it is shut. */}
        {active > 0 && onClear && (
          <button
            type="button"
            className="filters-clear"
            onClick={onClear}
            title={t('Clear the filters')}
            aria-label={t('Clear the filters')}
          >
            ✕
          </button>
        )}
      </div>
      {open && (
        <div className="filters" id={bodyId}>
          {children}
        </div>
      )}
    </section>
  )
}

/**
 * The last row of every Filters panel, the same on every page: whatever the page offers
 * there first (the date presets, usually), then the one Clear filters button — disabled
 * when nothing is applied, so it never looks like it did something it did not — and the
 * spinner while a change is being fetched, after an optional `note` such as "12 of 400
 * shown". Pages used to write this row themselves, and
 * it came out as "Clear filters", "Clear (2)" and nothing at all.
 */
export const FilterActions = ({ active = 0, onClear, busy, note, children }) => (
  <div className="filters-actions">
    {children}
    {onClear && (
      <button type="button" className="btn btn-sm" onClick={onClear} disabled={!active}>
        Clear filters
      </button>
    )}
    <span className="spacer" />
    {note && <span className="small subtle nums">{note}</span>}
    {busy && <span className="spinner" />}
  </div>
)

// ---------------------------------------------------------------- sorting

/**
 * Which column a list is sorted by, and which way. Clicking the sorted column turns it
 * round; clicking another sorts by it, newest or largest first for the keys in `descFirst`
 * (dates and counts), A to Z for the rest — the same rule the Updates table follows.
 */
export function useSort(initialKey, initialDir = 'asc', descFirst = []) {
  const [sort, setSort] = useState(initialKey)
  const [dir, setDir] = useState(initialDir)
  const onSort = (key) => {
    if (sort === key) setDir((d) => (d === 'desc' ? 'asc' : 'desc'))
    else {
      setSort(key)
      setDir(descFirst.includes(key) ? 'desc' : 'asc')
    }
  }
  return { sort, dir, onSort }
}

/**
 * A column heading that sorts — the one DataTable draws for every column it is given.
 * A list that sorts by its headings goes through DataTable rather than writing its own
 * <table> round this, so that it gets the rows and the pager the same way too.
 *
 * The control is a real button inside the <th>, so it can be reached with Tab and pressed
 * with Enter or Space; styles.css stretches its hit area over the whole cell, so a click
 * anywhere on the heading still sorts. aria-sort sits on the <th>, where the table's
 * semantics expect it, and only on the column actually sorted. The arrows are decoration:
 * aria-sort already says which way.
 */
export function SortTh({ label, sortKey, sort, dir, onSort, className = '', style }) {
  const t = useRelabel()
  const sortable = !!sortKey && !!onSort
  const isSorted = sortable && sort === sortKey
  return (
    <th
      className={`${className} ${sortable ? 'sortable' : ''} ${isSorted ? 'sorted' : ''}`.trim() || undefined}
      style={style}
      aria-sort={isSorted ? (dir === 'desc' ? 'descending' : 'ascending') : undefined}
    >
      {sortable ? (
        <button type="button" className="th-sort" onClick={() => onSort(sortKey)}>
          {t(label)}
          <span className="sort-arrow" aria-hidden="true">
            {isSorted ? (dir === 'desc' ? '▼' : '▲') : '⇅'}
          </span>
        </button>
      ) : (
        t(label)
      )}
    </th>
  )
}

// ---------------------------------------------------------------- pagination

/* `limits` and `limitLabel` let a page that is not a table offer its own choices — the
   Media Library counts pictures, not rows, and wants larger steps. */
const LIMITS = [25, 50, 100, 200, 500]

export function Pager({ page, pages, total, limit, onPage, onLimit, limits = LIMITS, limitLabel = 'Rows' }) {
  if (!total) return null
  const from = (page - 1) * limit + 1
  const to = Math.min(total, page * limit)
  return (
    <div className="pager">
      <span className="nums">
        {from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}
      </span>
      <span className="spacer" />
      {onLimit && (
        <span className="row row-tight small">
          <span aria-hidden="true">{limitLabel}</span>
          <EnumSelect
            value={limit}
            onChange={(v) => onLimit(Number(v))}
            options={limits.map((n) => ({ value: n, label: String(n) }))}
            isClearable={false}
            aria-label={`${limitLabel} per page`}
            compact
            sx={{ width: 80 }}
          />
        </span>
      )}
      <div className="btn-group">
        <button className="btn btn-sm" disabled={page <= 1} onClick={() => onPage(1)} title="First page">
          «
        </button>
        <button className="btn btn-sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          Prev
        </button>
        <button className="btn btn-sm" disabled>
          {page} / {pages || 1}
        </button>
        <button className="btn btn-sm" disabled={page >= pages} onClick={() => onPage(page + 1)}>
          Next
        </button>
        <button className="btn btn-sm" disabled={page >= pages} onClick={() => onPage(pages)} title="Last page">
          »
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- toasts

const ToastCtx = createContext(null)

/**
 * `action` is how a one-click change that overwrote something offers it back:
 * `{ label, onClick }` renders a button in the toast, and pressing it dismisses the
 * toast. A toast carrying one stays up longer, since it has to be read and acted on
 * rather than just noticed.
 */
export function ToastProvider({ children }) {
  const t = useRelabel()
  const [items, setItems] = useState([])
  const dismiss = useCallback((id) => setItems((xs) => xs.filter((x) => x.id !== id)), [])
  const push = useCallback(
    (message, tone = 'info', ms = 4500, action = null) => {
      const id = Math.random().toString(36).slice(2)
      setItems((xs) => [...xs, { id, message, tone, action }])
      setTimeout(() => dismiss(id), ms)
    },
    [dismiss]
  )
  const value = {
    push,
    ok: (m, action) => push(m, 'ok', action ? 10000 : 4500, action),
    error: (m) => push(typeof m === 'string' ? m : m?.message || 'Something went wrong', 'error', 8000),
    info: (m) => push(m, 'info'),
  }
  return (
    <ToastCtx.Provider value={value}>
      {children}
      <div className="toast-stack" aria-live="polite">
        {items.map((item) => (
          <div key={item.id} className={`toast toast-${item.tone}`}>
            <Icon name={item.tone === 'ok' ? 'check' : item.tone === 'error' ? 'error' : 'info'} size={16} />
            {/* Every toast is written by this application or by the Worker, which is
                how a server message comes to use the installation's term. */}
            <span style={{ minWidth: 0 }}>{t(item.message)}</span>
            {item.action && (
              <button
                className="btn btn-sm toast-action"
                onClick={() => {
                  item.action.onClick()
                  dismiss(item.id)
                }}
              >
                {t(item.action.label)}
              </button>
            )}
            <button
              className="btn btn-ghost btn-icon btn-sm"
              onClick={() => dismiss(item.id)}
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  )
}

export const useToast = () => {
  const ctx = useContext(ToastCtx)
  if (!ctx) throw new Error('useToast must be used inside ToastProvider')
  return ctx
}

// ---------------------------------------------------------------- formatting

export const fmtInt = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString())

/**
 * How many people a partner unit is, and where the number comes from.
 *
 * Most units are one person; a good number are a married couple serving together, which
 * the registry counts as one unit and two partners. The file splits them into staff and
 * non-staff and this installation treats both as partners, so the number shown is the
 * sum — see migration 0026. The breakdown goes in a `title`, because it is the answer to
 * a question almost nobody is asking and the sum is the one everybody is.
 *
 * `null` for a unit nobody has counted, which is not a unit with nobody in it: every
 * unit reads null until a file carrying the two new columns has been imported over it.
 * Callers render that as the em dash `fmtInt` gives it rather than as a zero.
 */
export function headcount(p) {
  const total = p?.partners_in_unit ?? null
  if (total === null) return { total: null, label: '—', title: 'No head count in the registry file yet' }
  const staff = p.staff_partners ?? 0
  const nonStaff = p.non_staff_partners ?? 0
  return {
    total,
    label: fmtInt(total),
    title:
      `${fmtInt(total)} partner${total === 1 ? '' : 's'} in this unit` +
      ` — ${fmtInt(staff)} staff and ${fmtInt(nonStaff)} non-staff`,
  }
}

/*
 * One way of writing a date across the whole app: ISO, 2026-04-16 — the form every list,
 * card and table already used, and the one that sorts as it reads. The record header
 * used to say "Apr 16, 2026" and the audit log "8/22/2026, 9:05:13 PM", so a single
 * page could show the same day three ways.
 */
export const fmtDate = (d) => {
  if (!d) return '—'
  const day = String(d).slice(0, 10)
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : String(d)
}

/** A moment: the same ISO date, then the local time on a 12-hour clock — 2026-08-22 9:05 PM. */
export const fmtDateTime = (s) => {
  if (!s) return '—'
  const dt = new Date(String(s).replace(' ', 'T') + (String(s).endsWith('Z') ? '' : 'Z'))
  if (Number.isNaN(dt.getTime())) return String(s)
  const pad = (n) => String(n).padStart(2, '0')
  const date = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`
  const time = dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  return `${date} ${time}`
}

export const fmtBytes = (b) => {
  if (b === null || b === undefined) return '—'
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`
  return `${(b / 1024 / 1024).toFixed(2)} MB`
}

/** Always kilobytes, however small — image sizes are compared against a KB limit. */
export const fmtKb = (b) => {
  if (b === null || b === undefined) return '—'
  const kb = b / 1024
  return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`
}

export const relDays = (iso) => {
  if (!iso) return '—'
  const days = Math.floor((Date.now() - new Date(`${String(iso).slice(0, 10)}T00:00:00`)) / 86400000)
  if (days < 0) return `in ${-days}d`
  if (days === 0) return 'today'
  if (days < 31) return `${days}d ago`
  if (days < 365) return `${Math.floor(days / 30)}mo ago`
  return `${(days / 365).toFixed(1)}y ago`
}

export const ACTIVITY_TONE = { active: 'ok', slowing: 'warn', dormant: 'danger', none: undefined }

export const ActivityBadge = ({ activity }) => (
  <Badge tone={ACTIVITY_TONE[activity]} dot>
    {activity === 'none' ? 'no updates' : activity}
  </Badge>
)

/**
 * Country name + flag, linking through to the country page.
 *
 * Not for a view-only account: country records are not part of that view, and a link
 * that lands on "this page is not one of yours" is worse than the plain name. Decided
 * here rather than at each call site so the rule cannot be forgotten on a page written
 * later. PartnerLabel below needs no such rule — partner records are in every view.
 */
export const CountryLabel = ({ id, name, flag, link = true }) => {
  const canBrowse = useCanBrowse()
  if (!name) return <span className="subtle">—</span>
  const inner = (
    <>
      {flag && <span aria-hidden="true">{flag} </span>}
      {name}
    </>
  )
  return link && id && canBrowse ? <Link to={`/countries/${id}`}>{inner}</Link> : <span>{inner}</span>
}

/**
 * The way out of a record, for a detail page's `back` slot on PageHeader. Label it
 * whatever the sidebar and the list's own header call the route, so the button
 * names the place it lands rather than describing the motion.
 *
 * A plain link, not history.back(): the lists keep their filters in component state
 * rather than the URL, so there is nothing for a history entry to restore that a
 * fresh link would lose — and this still works when the record was opened cold from
 * a bookmark or a search result, where there is no back to go to.
 */
export const BackTo = ({ to, children }) => (
  <Link className="btn btn-sm" to={to}>
    ← <Relabelled>{children}</Relabelled>
  </Link>
)

export const PartnerLabel = ({ id, code, name, link = true }) => {
  /* The prefix is applied here rather than by the pages that use this: four lists and two
     reports hand a raw code to this component, and putting it at each of those call sites
     would be six chances to leave one drawing S4CA while the rest of the archive had moved
     on. The comparison below stays against the raw code — `name !== code` is asking
     whether the unit has a name of its own, which is a fact about the record and not
     about how it is spelled on screen. */
  const withPrefix = useCode()
  const shown = withPrefix(code)
  const inner = (
    <>
      <span className="mono subtle">{shown}</span>
      {/* The name too, because four units in this archive have no name of their own and
          carry their code in the field instead — and one spelling beside the other reads
          as a fault. Harmless on a real name: the swap only touches a string that starts
          with the stored prefix. */}
      {name && name !== code ? <span> {withPrefix(name)}</span> : null}
    </>
  )
  return link && id ? <Link to={`/partners/${id}`}>{inner}</Link> : <span>{inner}</span>
}

/** Download an array of objects as CSV — used by the export buttons. */
export function downloadCsv(filename, rows, columns) {
  if (!rows?.length) return
  // The heading row is written by the page that exports, so it follows the
  // installation's term the way the table on screen does. Read from outside React:
  // this is a plain function called from a click handler.
  const term = currentTerm()
  const cols = (columns ?? Object.keys(rows[0]).map((k) => ({ key: k, label: k }))).map((c) =>
    typeof c.label === 'string' ? { ...c, label: relabelText(c.label, term) } : c
  )
  // Quoting, and keeping a typed-in name from being read as a formula — see lib/csv.js.
  const esc = csvCell
  /* A code column is written in the installation's prefix, the same as the table it was
     exported from. Done here rather than by each caller because every export that carries
     a code names the column one of these two ways, and a spreadsheet showing S4CA-030
     under a heading the screen calls ABC-030 is the kind of disagreement nobody chases
     down until it has been pasted into a report. The value keyed off the column, not
     sniffed out of the data: a country called S4CA-something is not a code. */
  const isCode = (key) => key === 's4ca_code' || key === 'code_and_name'
  const cell = (c, r) => {
    const v = typeof c.get === 'function' ? c.get(r) : r[c.key]
    return isCode(c.key) ? codeText(v, term) : v
  }
  const csv = [
    cols.map((c) => esc(c.label)).join(','),
    ...rows.map((r) => cols.map((c) => esc(cell(c, r))).join(',')),
  ].join('\n')
  const url = URL.createObjectURL(new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' }))
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
